import Account from './containers/account'
import {
    SignUp,
    SignIn,
    SignOut,
    Activate,
    ForgotPassword,
    ResetPassword,
} from './components/account'
import Profile from './containers/profile'
import OnboardingInstructions from './containers/onboarding_instructions'
import ICA from './containers/ica'
// import PaymentDetails from './containers/payment_details'
// import PaymentDetailsEdit from './containers/payment_details_edit'
import Jobs from './containers/jobs'
import JobHistory from './containers/job_history'
import Invoice from './containers/invoice'
import Personal from './containers/personal'
import Experience from './containers/experience'
import CrewPositions from './containers/crew_positions'
import Gear from './containers/gear'
import Document from './containers/document'
import FourOhFour from './components/404'

import { storage } from './utils'
import { IMPERSONATE_URL } from './constants/APIEndpoints'

import { authenticate } from './actions/account'

const ONBOARDING_STEPS = {
    '/ica': 0,
    '/jobs': 1,         // allowed once ICA is signed
    '/jobs/history': 1, // allowed once ICA is signed
    '/job': 1,          // allowed once ICA is signed
    '/personal': 1,
    '/experience': 2,
    '/crew-positions': 3,
    '/gear/primary': 4,
    '/gear/secondary': 5,
    '/gear/tertiary': 6,
    '/profile': 100, // only allowed after onboarding
    '/': 100, // in order to redirect '/' to the right place during onboarding
}

function handleRedirect(onboardingData, routerState, replace) {
    if (onboardingData) {
        const route = routerState.location.pathname

        // If onboarding is fully complete and we're going to the root, go to jobs section.
        if (onboardingData.onboarding_complete) {
            if (route === '/') {
                replace('/jobs')
            }

            // Otherwise go where we're asked.
            return
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/ica']) {
            if (! onboardingData.ica_complete) {
                replace('/onboarding-instructions')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/personal']) {
            if (! onboardingData.personal_complete) {
                replace('/personal')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/experience']) {
            if (! onboardingData.experience_complete) {
                replace('/experience')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/crew-positions']) {
            if (! onboardingData.crew_positions_complete) {
                replace('/crew-positions')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/gear/primary']) {
            if (! onboardingData.gear_primary_complete) {
                replace('/gear/primary')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/gear/secondary']) {
            if (! onboardingData.gear_secondary_complete) {
                replace('/gear/secondary')
                return
            }
        }

        if (ONBOARDING_STEPS[route] > ONBOARDING_STEPS['/gear/tertiary']) {
            if (! onboardingData.gear_tertiary_complete) {
                replace('/gear/tertiary')
                return
            }
        }
    }
}

export default function getRoutes(store) {
    const authoriseRoute = (nextState, replace, cb) => {
        // At this point we may be in one of two states.
        //
        // 1. We already have all the onboarding data loaded in. In which case, we just pass
        //    that to the `handleRedirect` function.
        //
        // 2. We have nothing but the auth token. So first we have to authenticate to get
        //    the crew UUID and onboarding data and then hand that to the `handleRedirect`
        //    function.

        let unsubscribe = null
        let authenticated = false

        const listener = () => {
            const { account } = store.getState()

            if (! (account.user && account.user.crew)) {
                // No usable data except for the token, so authenticate and return.
                if (! authenticated) {
                    authenticated = true
                    store.dispatch(authenticate())
                }

                return
            }

            // `onboarding_complete` here just to check existence of data for checking, not
            // actual data check (that's done in `handleRedirect`).
            if (Object.keys(account.user.crew).includes('onboarding_complete')) {
                // If we have onboarding data, unsubscribe the subscription and
                // handle the redirect.
                unsubscribe && unsubscribe()
                handleRedirect(account.user.crew, nextState, replace)
                cb()
                return
            }
        }

        unsubscribe = store.subscribe(listener)
        listener()
    }

    const impersonate = (nextState, replace, cb) => {
        const adminToken = nextState.params.token
        const userUUID = nextState.params.user_uuid

        // Rather than dispatching actions, etc. here, we will recreate the 'sign in' action logic.
        // This is mainly because we are doing this logic in a router onEnter callback, and waiting
        // for the store to update will require store subscriptions, which I'd rather avoid. Plus,
        // we can tailor the code to be specific to the impersonation API this way, too.
        const request = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Token ${ adminToken }`,
            },
            body: JSON.stringify({ user_uuid: userUUID }),
        }

        fetch(IMPERSONATE_URL, request).then((response) => response.json()).then((data) => {
            // Set the auth token to be the impersonating user's (i.e. the admin's) token.
            storage.setItem('authToken', adminToken)

            // Set the impersonation token to be the actual user's token. This is used when re-authenticating.
            storage.setItem('impersonationToken', data.token)

            // Set up the user data by emulating a sign in
            store.dispatch(
                { type: 'SIGN_IN_SUCCESS', data }
            )

            // Finally, redirect home so the app can do its normal sign in logic.
            replace('/')
            return cb()
        }).catch(() => {
            // Even on error (i.e. bad auth data or user UUID) we should redirect to the home route,
            // just so that the site doesn't appear to be broken. It'll just be the 'sign in' page.
            replace('/')
            return cb()
        })
    }

    return {
        path: '/',
        component: Account,
        indexRoute: {
            component: FourOhFour,
            onEnter: authoriseRoute,
        },
        childRoutes: [
            { path: '/sign-up', component: SignUp },
            { path: '/sign-in', component: SignIn },
            { path: '/sign-out', component: SignOut },
            { path: '/activate/:uuid/:token/', component: Activate },
            { path: '/impersonate/:token/:user_uuid', onEnter: impersonate },
            { path: '/forgot-password', component: ForgotPassword },
            { path: '/reset-password/:uuid/:token/', component: ResetPassword },
            { path: '/onboarding-instructions', component: OnboardingInstructions },
            { path: '/ica', component: ICA, onEnter: authoriseRoute },
            // { path: '/banking', component: PaymentDetails, onEnter: authoriseRoute },
            // { path: '/banking/edit', component: PaymentDetailsEdit, onEnter: authoriseRoute },

            { path: '/jobs', component: Jobs, onEnter: authoriseRoute },
            { path: '/jobs/history', component: JobHistory, onEnter: authoriseRoute },
            { path: '/job/:job/invoice', component: Invoice, onEnter: authoriseRoute },

            { path: '/profile', component: Profile, onEnter: authoriseRoute },
            { path: '/personal', component: Personal, onEnter: authoriseRoute },
            { path: '/experience', component: Experience, onEnter: authoriseRoute },
            { path: '/crew-positions', component: CrewPositions, onEnter: authoriseRoute },
            { path: '/gear/:type', component: Gear, onEnter: authoriseRoute },

            // static documentation pages
            { path: '/about', component: Document, slug: 'about' },
            { path: '/billing-instructions', component: Document, slug: 'billing-instructions' },
            { path: '/code-of-conduct', component: Document, slug: 'code-of-conduct' },
            { path: '/production-guidelines', component: Document, slug: 'production-guidelines' },
            { path: '/faqs', component: Document, slug: 'faq' },
            { path: '/privacy-policy', component: Document, slug: 'privacy-policy' },
            { path: '/terms-of-use', component: Document, slug: 'terms' },
            { path: '*', component: FourOhFour },
        ],
    }
}



// WEBPACK FOOTER //
// ./app/routes.js